import React from 'react';
import AppActionMenu from '../../../../../components/AppActionMenu';
import { getStatusBadgeClass } from '../../../../../constants/statusColors';

export default function AttendanceRegularizationTable({
  records = [],
  isLoading = false,
  onView,
  onEdit,
  onDelete,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-xs font-semibold text-on-surface-variant">Loading regularization requests...</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-64 w-full flex-col items-center justify-center rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-8 text-center">
        <span className="material-symbols-outlined mb-2 text-4xl text-outline-variant">
          pending_actions
        </span>
        <h3 className="font-display text-sm font-bold text-black">No Regularization Records Found</h3>
        <p className="mt-1 text-xs text-slate-600 max-w-sm">
          No regularization requests found for the selected status and date period.
        </p>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-outline-variant/25 bg-surface-container-low/40 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-6 text-black">NAME</th>
              <th className="py-3.5 px-6 text-black">DATE TIME</th>
              <th className="py-3.5 px-6 text-black">REASON</th>
              <th className="py-3.5 px-6 text-black">REMARKS</th>
              <th className="py-3.5 px-6 text-center text-black">STATUS</th>
              <th className="py-3.5 px-6 text-right w-20 text-black">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((record, index) => {
              return (
                <tr
                  key={record.id || index}
                  className="transition-colors hover:bg-surface-container-low/50 group"
                >
                  {/* NAME (First column) */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="font-bold text-sm text-black font-sans not-italic">
                        {record.name || 'Divyanshu Singh'}
                      </span>
                      {record.empId && (
                        <span className="text-xs text-slate-600 font-sans tabular-nums not-italic font-semibold ml-1.5">
                          ({record.empId})
                        </span>
                      )}
                    </div>
                  </td>

                  {/* DATE TIME */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-black font-sans tabular-nums not-italic font-medium text-sm">
                      <span className="material-symbols-outlined text-base text-primary">
                        schedule
                      </span>
                      <span>{record.dateTime || `${record.date} ${record.time || ''}`}</span>
                    </div>
                  </td>

                  {/* REASON */}
                  <td className="py-4 px-6 font-bold text-sm text-black font-sans not-italic whitespace-nowrap">
                    {record.reason || 'Others'}
                  </td>

                  {/* REMARKS (Crisp black text, highly visible) */}
                  <td className="py-4 px-6 text-sm text-black font-medium font-sans not-italic max-w-xs truncate">
                    {record.remarks || '—'}
                  </td>

                  {/* STATUS (Matching Pay Heads soft-tint pill format) */}
                  <td className="py-4 px-6 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${getStatusBadgeClass(
                        record.status
                      )}`}
                    >
                      {record.status}
                    </span>
                  </td>

                  {/* ACTIONS with 3-dot dropdown (Order: View, Edit, Delete) */}
                  <td className="py-4 px-6 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1 text-[#4b5563] hover:text-black hover:bg-surface-container-high transition bg-transparent cursor-pointer"
                        items={[
                          {
                            key: 'view',
                            label: 'View',
                            icon: 'visibility',
                            onClick: () => onView?.(record),
                          },
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            onClick: () => onEdit?.(record),
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: 'delete',
                            onClick: () => onDelete?.(record.id),
                          },
                        ]}
                        onSelect={(key) => {
                          if (key === 'view') onView?.(record);
                          if (key === 'edit') onEdit?.(record);
                          if (key === 'delete') onDelete?.(record.id);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
